import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { IMaskInput } from 'react-imask';
import { SIDE_DRAW_MODAL_TYPE } from '../../../../../const/modalType';

import { Counter } from '../../../../Counter/';
import { INVALIDATION_TIP } from '../../../../../const/validation';
import { useStores } from '../../../../../hooks';
import { format_price } from '../../../../../js/utils';
import { ButtonCustom } from '../../../../ButtonCustom';
import { FormLabel } from '../../../../FormLabel';
import { SelectCustom } from '../../../../SelectCustom';
import { TextInput } from '../../../../TextInput';
import { makeSelectOption } from '../../utils';

import styles from './styles/get_gift_form_modal.module.scss';
import inputStyles from '../../../../TextInput/styles/text_input.module.scss';

export const GetGiftFormModal = observer(() => {
  const [errorText, setErrorText] = useState('');
  const [errorButtonLink, setErrorButtonLink] = useState('');
  const [isError, setIsError] = useState(false);
  const { modalStore, userStore, loyaltyStore } = useStores();

  const location = useLocation();

  const props = modalStore.modalsProps[modalStore.currentModal];

  const { currentUser } = userStore;

  const { first_name: name, email, phone, company } = currentUser;

  const { buyGift } = loyaltyStore;

  const nominalOptions = useMemo(
    () => props.gift.nominals.map((el) => makeSelectOption(el)),
    [props],
  );

  const { register, handleSubmit, watch, setValue, control } = useForm({
    defaultValues: {
      ...props,
      name,
      email,
      phone,
      company,
    },
  });

  const handleCounterChange = (value) => {
    setValue('count', value);
  };

  const handleFormSubmit = async ({ count, nominal }) => {
    try {
      await buyGift({ count, nominal_id: nominal.id });
      modalStore.close();
      handleOpenModal();
    } catch (error) {
      const { response } = error;
      if (response !== undefined) {
        setIsError(true);
        Object.keys(response.data).forEach((key) => {
          setErrorText(response.data[key]);
        });
      }
    }
  };

  const handleCloseModal = () => {
    modalStore.close();
  };

  const handleOpenModal = () => {
    modalStore.open('getGiftConfirm', {
      modalType: SIDE_DRAW_MODAL_TYPE.LOYALTY_PROGRAM,
    });
  };

  const allFields = watch();

  const totalPrice = useMemo(
    () => allFields.nominal?.value * allFields.count,
    [allFields],
  );

  const UserInfoRequired = [name, email, phone];

  const isFullUserInfo = useMemo(
    () => !UserInfoRequired.includes(''),
    UserInfoRequired,
  );

  const getLkPath = () => {
    const searchParams = new URLSearchParams({
      tab: 'personalData',
    }).toString();

    return `${location.pathname}?${searchParams}`;
  };

  useEffect(() => {
    if (!isFullUserInfo) {
      setErrorText(INVALIDATION_TIP.FILL_YOURSELF_INFORMATION);
      setErrorButtonLink('личном кабинете');
    }
  }, [isFullUserInfo]);

  return (
    <form className={styles.form} onSubmit={handleSubmit(handleFormSubmit)}>
      <div className={styles.form__header}>
        <h3 className={styles.form__title}>Получить подарок</h3>
        <span className={styles.form__subtitle}>
          {props.gift?.title ?? ''}, {allFields.nominal?.label ?? ''}
        </span>
      </div>

      <div className={styles.form__inputs}>
        <div className={styles.form__item}>
          <FormLabel text="Номинал" />
          <Controller
            control={control}
            name="nominal"
            render={({ field: { onChange, value } }) => (
              <SelectCustom
                options={nominalOptions}
                onChange={onChange}
                value={value}
                selectCustomStyles={{ maxWidth: '100%' }}
              />
            )}
          />
        </div>

        <div className={styles.form__item}>
          <FormLabel text="Количество" />
          <Counter
            value={allFields.count}
            name="count"
            onChange={handleCounterChange}
            onBlur={handleCounterChange}
            max={999}
            register={register}
          />
        </div>
        <div className={styles.form__item}>
          <FormLabel text="Имя" isRequired />
          <TextInput {...register('name')} disabled name="name" type="text" />
        </div>
        <div className={styles.form__item}>
          <FormLabel text="Email" isRequired />
          <TextInput
            {...register('email')}
            disabled
            name="email"
            type="email"
          />
        </div>
        <div className={styles.form__item}>
          <FormLabel text="Номер телефона для связи" isRequired />
          <Controller
            id="phone"
            name="phone"
            type="tel"
            control={control}
            render={({ field: { onChange, value } }) => (
              <IMaskInput
                className={inputStyles.input}
                mask={'+{7} 000 000-00-00'}
                unmask={true}
                value={value}
                onAccept={onChange}
                disabled
              />
            )}
          />
        </div>
        <div className={styles.form__item}>
          <FormLabel text="Компания" />
          <TextInput
            {...register('company')}
            disabled
            name="name"
            type="text"
          />
        </div>
        <div className={styles.form__price_container}>
          <div
            className={classNames({
              [styles.form__item]: true,
              [styles.form__price_item]: true,
            })}
          >
            <FormLabel text="Общая стоимость" />
            <span className={styles.form__price_amount}>
              {format_price(totalPrice)} ₽
            </span>
          </div>
        </div>
        <div className={styles.form__buttons}>
          <ButtonCustom
            text="Закрыть"
            variant="outlined"
            type="button"
            onClick={handleCloseModal}
          />
          <ButtonCustom
            disabled={!isFullUserInfo}
            text="Получить подарок"
            type="submit"
          />
        </div>
        {(!isFullUserInfo || isError) && (
          <div className={styles.form__errors}>
            <span className={styles.form__error}>{errorText}</span>
            <Link
              to={getLkPath()}
              className={styles.form__error_linkLk}
              onClick={() => {
                handleCloseModal();
              }}
            >
              {errorButtonLink}
            </Link>
          </div>
        )}
      </div>
    </form>
  );
});
